import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Icon from '../Icon/Icon';
import Notice from '../Notice/Notice';

/**
 * `TextArea` is an extension of the HTML text area input field and allows multiline text to be entered.
 *
 * An optional character count can be shown in the upper right corner of the text area.
 *
 * An optional error message can be displayed below the text area.
 *
 * @property {string} characterCountText The character count label. Defaults to "#".
 * @property {string} className The class name of this component. Will be appended to "core-textarea".
 * @property {node} errorMessage An error {@link Notice} that will be shown below the component. Defaults to no Notice.
 * @property {node} warningMessage A warning Notice that will be shown below the component. Defaults to no Notice.
 * @property {node} successMessage A success Notice that will be shown below the component. Defaults to no Notice.
 * @property {node} infoMessage An info Notice that will be shown below the component. Defaults to no Notice.
 * @property {string} id The ID of this component. Defaults to variant of the label provided.
 * @property {string} label The label of the input field. Defaults to no label.
 * @property {boolean} locked The text area will be disabled and show a small lock icon in the upper right corner.
 * @property {number} maxLength The maximum number of characters that can be entered into this input. Defaults to unlimited
 * length.
 * @property {function} onBlur A callback that will be called when the component has lost focus.
 * @property {function} onFocus A callback that is called when the component gets the input focus.
 * @property {function} onClearNotice Callback that is called when the user dismiss the optional Notice.
 * @property {string} placeholder The placeholder text for this text area. Defaults to no placeholder.
 * @property {number} rows The number of rows to display by default. Defaults to 4 rows.
 * @property {boolean} showCharacterCount If set to `true`, the text area will show a character count in the upper right
 * corner. Defaults to `false`.
 * @property {boolean} showUnderline The component will display a line under the text area to indicate input focus. You
 * can set the value of this prop to `false` to turn this off. Defaults to `true`.
 * @property {string} title A value which is used as an HTML title attribute for the textarea field.
 * @property {string} value The value of this text area.
 *
 * @see Input
 */
export default class TextArea extends PureComponent {

  static displayName = 'TextArea';

  static propTypes = {
    characterCountText: PropTypes.string,
    className: PropTypes.string,
    errorMessage: PropTypes.node, // eslint-disable-line
    id: PropTypes.string,
    infoMessage: PropTypes.node, // eslint-disable-line
    label: PropTypes.string,
    locked: PropTypes.bool,
    maxLength: PropTypes.number,
    onBlur: PropTypes.func,
    onClearNotice: PropTypes.func,
    onFocus: PropTypes.func,
    placeholder: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
    rows: PropTypes.number,
    showCharacterCount: PropTypes.bool,
    showUnderline: PropTypes.bool,
    successMessage: PropTypes.node, // eslint-disable-line
    title: PropTypes.string,
    value: PropTypes.string, // eslint-disable-line react/no-unused-prop-types
    warningMessage: PropTypes.node,
  };

  static defaultProps = {
    characterCountText: '#',
    rows: 4,
    showUnderline: true,
    value: '',
  };

  onBlur = e => {
    this.setState({ focus: false });
    if (this.props.onBlur) {
      this.props.onBlur(e);
    }
  };

  onFocus = e => {
    this.setState({ focus: true });
    if (this.props.onFocus) {
      this.props.onFocus(e);
    }
  };

  render() {
    const { characterCountText, className, errorMessage, id, infoMessage, label, locked, maxLength,
      onClearNotice, showCharacterCount, showUnderline, successMessage, title, value, warningMessage, ...rest } = this.props;
    const inputId = id || (label && label.toLowerCase().replace(/ /g, '-')); // HACK label should be outside Input component
    const cls = cx('core-textarea', className, {
      count: showCharacterCount,
      focus: this.state && this.state.focus,
      label,
      'no-underline': !showUnderline,
      'has-errors': errorMessage || warningMessage || successMessage || infoMessage,
    });

    return (
      <div className={cls}>
        {label && <label htmlFor={inputId} title={title} className="label">{label}</label>}
        <textarea title={title}  id={inputId} dir="auto" {...rest} maxLength={maxLength} disabled={rest.disabled || locked} onFocus={this.onFocus} onBlur={this.onBlur} value={value}/>
        {locked && <Icon name="lock"/>}
        {showCharacterCount && !locked && <span className="char-count">{characterCountText}{value ? value.length : 0} {maxLength ? `/ ${maxLength}` : '' }</span>}
        <Notice
          errorMessage={errorMessage}
          warningMessage={warningMessage}
          successMessage={successMessage}
          infoMessage={infoMessage}
          onClear={onClearNotice}/>
      </div>
    );
  }

}
